<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import FeatureLockedScreen from 'dashboard/components/FeatureLockedScreen.vue';
import { conversionsApi, conversionTypesApi } from 'dashboard/api/conversions';

const agents = useMapGetter('agents/getAgents');

const { t } = useI18n();
const { accountId } = useAccount();
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');
const hasConversions = computed(() =>
  isFeatureEnabled.value(accountId.value, 'conversions')
);

// Tabs
const activeTab = ref('list');
const tabs = computed(() => [
  { id: 'list', label: t('CONVERSIONS.TITLE'), icon: 'i-lucide-trending-up' },
  {
    id: 'analytics',
    label: t('CONVERSIONS.ANALYTICS.TITLE'),
    icon: 'i-lucide-bar-chart-3',
  },
  { id: 'types', label: t('CONVERSIONS.TYPES.TITLE'), icon: 'i-lucide-tag' },
]);

// ==== CONVERSIONS LIST ====
const conversions = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const totalPages = ref(1);
const loadingConversions = ref(false);
const filters = ref({
  source_type: '',
  conversion_type_id: '',
  start_date: '',
  end_date: '',
  contact_id: '',
  user_id: '',
});

async function fetchConversions() {
  loadingConversions.value = true;
  try {
    const params = { page: currentPage.value };
    if (filters.value.source_type)
      params.source_type = filters.value.source_type;
    if (filters.value.conversion_type_id)
      params.conversion_type_id = filters.value.conversion_type_id;
    if (filters.value.start_date) params.start_date = filters.value.start_date;
    if (filters.value.end_date) params.end_date = filters.value.end_date;
    if (filters.value.contact_id) params.contact_id = filters.value.contact_id;
    if (filters.value.user_id) params.user_id = filters.value.user_id;
    const res = await conversionsApi.list(params);
    conversions.value = res.data?.payload || [];
    totalCount.value = res.data?.meta?.count || 0;
    totalPages.value = res.data?.meta?.total_pages || 1;
  } catch (e) {
    conversions.value = [];
  } finally {
    loadingConversions.value = false;
  }
}

function applyFilters() {
  currentPage.value = 1;
  fetchConversions();
  fetchListTotals();
}

function clearFilters() {
  filters.value = {
    source_type: '',
    conversion_type_id: '',
    start_date: '',
    end_date: '',
    contact_id: '',
    user_id: '',
  };
  currentPage.value = 1;
  fetchConversions();
  fetchListTotals();
}

// ==== LIST TOTALS (financial summary for list tab) ====
const listTotals = ref({
  count: 0,
  value: 0,
  avg_value: 0,
  crm_count: 0,
  agenda_count: 0,
});

async function fetchListTotals() {
  try {
    const params = {};
    if (filters.value.source_type) params.source = filters.value.source_type;
    if (filters.value.start_date) params.start_date = filters.value.start_date;
    if (filters.value.end_date) params.end_date = filters.value.end_date;
    const res = await conversionsApi.analytics(params);
    listTotals.value = res.data?.totals || {
      count: 0,
      value: 0,
      avg_value: 0,
      crm_count: 0,
      agenda_count: 0,
    };
  } catch {
    // silent
  }
}

// ==== ANALYTICS ====
const analyticsData = ref({ by_contact: [], by_agent: [], by_product: [], totals: {} });
const loadingAnalytics = ref(false);
const analyticsSeparated = ref('all');

async function fetchAnalytics() {
  loadingAnalytics.value = true;
  try {
    const params = {};
    if (analyticsSeparated.value !== 'all')
      params.source = analyticsSeparated.value;
    if (filters.value.start_date) params.start_date = filters.value.start_date;
    if (filters.value.end_date) params.end_date = filters.value.end_date;
    if (filters.value.user_id) params.user_id = filters.value.user_id;
    const res = await conversionsApi.analytics(params);
    analyticsData.value = res.data || { by_contact: [], by_agent: [], totals: {} };
  } catch (e) {
    analyticsData.value = { by_contact: [], by_agent: [], totals: {} };
  } finally {
    loadingAnalytics.value = false;
  }
}

watch(analyticsSeparated, () => fetchAnalytics());

// ==== PIPELINE PERFORMANCE ====
const pipelinePerformance = ref([]);
const pipelineMode = ref('current');
const loadingPipelinePerf = ref(false);
const pipelineFilters = ref({ start_date: '', end_date: '' });
const expandedPipelines = ref({});

async function fetchPipelinePerformance() {
  loadingPipelinePerf.value = true;
  try {
    const params = {};
    if (pipelineFilters.value.start_date)
      params.start_date = pipelineFilters.value.start_date;
    if (pipelineFilters.value.end_date)
      params.end_date = pipelineFilters.value.end_date;
    const res = await conversionsApi.pipelinePerformance(params);
    pipelinePerformance.value = res.data?.by_pipeline || [];
    pipelineMode.value = res.data?.mode || 'current';
  } catch {
    pipelinePerformance.value = [];
  } finally {
    loadingPipelinePerf.value = false;
  }
}

function applyPipelineFilters() {
  fetchPipelinePerformance();
}

function clearPipelineFilters() {
  pipelineFilters.value = { start_date: '', end_date: '' };
  fetchPipelinePerformance();
}

// ==== CONVERSION TYPES ====
const conversionTypes = ref([]);
const loadingTypes = ref(false);
const showTypeForm = ref(false);
const editingType = ref(null);
const agendaBookingStatusOptions = [
  { value: 'completed', label: 'Concluído' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'no_show', label: 'Não compareceu' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'pending', label: 'Pendente' },
];

const typeForm = ref({
  name: '',
  description: '',
  color: '#6366f1',
  has_monetary_value: true,
  source_category: 'general',
  agenda_booking_statuses: [],
});

async function fetchTypes() {
  loadingTypes.value = true;
  try {
    const res = await conversionTypesApi.list();
    conversionTypes.value = res.data?.payload || [];
  } catch (e) {
    conversionTypes.value = [];
  } finally {
    loadingTypes.value = false;
  }
}

function openNewType() {
  editingType.value = null;
  typeForm.value = {
    name: '',
    description: '',
    color: '#6366f1',
    has_monetary_value: true,
    source_category: 'general',
    agenda_booking_statuses: [],
  };
  showTypeForm.value = true;
}

function openEditType(type) {
  editingType.value = type;
  typeForm.value = {
    name: type.name,
    description: type.description || '',
    color: type.color,
    has_monetary_value: type.has_monetary_value,
    source_category: type.source_category || 'general',
    agenda_booking_statuses: Array(type.agenda_booking_statuses || []),
  };
  showTypeForm.value = true;
}

async function saveType() {
  if (!typeForm.value.name.trim()) return;
  try {
    const payload = {
      ...typeForm.value,
      agenda_booking_statuses:
        typeForm.value.source_category === 'agenda'
          ? typeForm.value.agenda_booking_statuses
          : [],
    };
    if (editingType.value) {
      await conversionTypesApi.update(editingType.value.id, payload);
    } else {
      await conversionTypesApi.create(payload);
    }
    showTypeForm.value = false;
    fetchTypes();
  } catch (e) {
    // error handled silently
  }
}

async function deleteType(type) {
  if (!window.confirm(t('CONVERSIONS.TYPES.DELETE_CONFIRM'))) return;
  try {
    await conversionTypesApi.delete(type.id);
    fetchTypes();
  } catch (e) {
    // error
  }
}

// ==== NEW CONVERSION FORM ====
const showConversionForm = ref(false);
const editingConversion = ref(null);
const conversionForm = ref({
  conversion_type_id: '',
  value: '',
  notes: '',
  converted_at: new Date().toISOString().split('T')[0],
});
const savingConversion = ref(false);

function openNewConversion() {
  editingConversion.value = null;
  conversionForm.value = {
    conversion_type_id: '',
    value: '',
    notes: '',
    converted_at: new Date().toISOString().split('T')[0],
  };
  showConversionForm.value = true;
}

function openEditConversion(conversion) {
  editingConversion.value = conversion;
  conversionForm.value = {
    conversion_type_id: conversion.conversion_type_id || '',
    value: conversion.value || '',
    notes: conversion.notes || '',
    converted_at:
      conversion.converted_at?.split('T')[0] ||
      new Date().toISOString().split('T')[0],
  };
  showConversionForm.value = true;
}

async function saveConversion() {
  savingConversion.value = true;
  try {
    const data = { ...conversionForm.value };
    if (data.value === '') delete data.value;
    if (editingConversion.value) {
      await conversionsApi.update(editingConversion.value.id, data);
    } else {
      await conversionsApi.create(data);
    }
    showConversionForm.value = false;
    fetchConversions();
  } catch (e) {
    // error
  } finally {
    savingConversion.value = false;
  }
}

async function deleteConversion(conversion) {
  if (!window.confirm(t('CONVERSIONS.DELETE_CONFIRM'))) return;
  try {
    await conversionsApi.delete(conversion.id);
    fetchConversions();
  } catch (e) {
    // error
  }
}

// ==== HELPERS ====
function formatCurrency(value) {
  if (!value && value !== 0) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR');
}

function sourceLabel(sourceType) {
  if (sourceType === 'PipelineItem') return t('CONVERSIONS.SOURCE_CRM');
  if (sourceType === 'AppointmentBooking')
    return t('CONVERSIONS.SOURCE_AGENDA');
  return t('CONVERSIONS.SOURCE_MANUAL');
}

function sourceColor(sourceType) {
  if (sourceType === 'PipelineItem') return 'bg-blue-100 text-blue-700';
  if (sourceType === 'AppointmentBooking')
    return 'bg-emerald-100 text-emerald-700';
  return 'bg-amber-100 text-amber-700';
}

function conversionTypeSourceLabel(type) {
  if (type?.source_category === 'agenda')
    return t('CONVERSIONS.TYPES.SOURCE_AGENDA');
  if (type?.source_category === 'crm') return t('CONVERSIONS.TYPES.SOURCE_CRM');
  return t('CONVERSIONS.TYPES.SOURCE_GENERAL');
}

// ==== FUNNEL HELPERS ====
function funnelWidth(stage, stages) {
  const max = Math.max(...stages.map(s => s.entries_count || 0), 1);
  const pct = ((stage.entries_count || 0) / max) * 100;
  return Math.max(30, Math.min(100, pct));
}

function hexToRgba(hex, alpha) {
  const clean = (hex || '#6366f1').replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) || 99;
  const g = parseInt(clean.substring(2, 4), 16) || 102;
  const b = parseInt(clean.substring(4, 6), 16) || 241;
  return `rgba(${r},${g},${b},${alpha})`;
}


onMounted(() => {
  fetchConversions();
  fetchListTotals();
  fetchTypes();
  fetchAnalytics();
  fetchPipelinePerformance();
});

watch(activeTab, tab => {
  if (tab === 'list') {
    fetchConversions();
    fetchListTotals();
  }
  if (tab === 'analytics') {
    fetchAnalytics();
    fetchPipelinePerformance();
  }
  if (tab === 'types') fetchTypes();
});
</script>

<template>
  <FeatureLockedScreen v-if="!hasConversions" feature-key="conversions" />
  <div v-else class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-n-weak flex-shrink-0"
    >
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('CONVERSIONS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          {{ t('CONVERSIONS.SUBTITLE') }}
        </p>
      </div>
      <button
        v-if="activeTab === 'list'"
        class="flex items-center gap-2 px-4 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        @click="openNewConversion"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('CONVERSIONS.NEW_CONVERSION') }}
      </button>
      <button
        v-if="activeTab === 'types'"
        class="flex items-center gap-2 px-4 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
        @click="openNewType"
      >
        <span class="i-lucide-plus size-4" />
        {{ t('CONVERSIONS.TYPES.NEW_TYPE') }}
      </button>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 px-6 pt-3 border-b border-n-weak flex-shrink-0">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors"
        :class="
          activeTab === tab.id
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        @click="activeTab = tab.id"
      >
        <span :class="tab.icon + ' size-4'" />
        {{ tab.label }}
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- LIST TAB -->
      <div v-if="activeTab === 'list'" class="p-6">
        <!-- Filters -->
        <div
          class="bg-n-alpha-1 border border-n-weak rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end"
        >
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FILTERS.ALL_SOURCES')
            }}</label>
            <select
              v-model="filters.source_type"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 h-8"
            >
              <option value="">
                {{ t('CONVERSIONS.FILTERS.ALL_SOURCES') }}
              </option>
              <option value="PipelineItem">
                {{ t('CONVERSIONS.SOURCE_CRM') }}
              </option>
              <option value="AppointmentBooking">
                {{ t('CONVERSIONS.SOURCE_AGENDA') }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FILTERS.ALL_TYPES')
            }}</label>
            <select
              v-model="filters.conversion_type_id"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 h-8"
            >
              <option value="">{{ t('CONVERSIONS.FILTERS.ALL_TYPES') }}</option>
              <option v-for="ct in conversionTypes" :key="ct.id" :value="ct.id">
                {{ ct.name }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FILTERS.START_DATE')
            }}</label>
            <input
              v-model="filters.start_date"
              type="date"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 h-8"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FILTERS.END_DATE')
            }}</label>
            <input
              v-model="filters.end_date"
              type="date"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 h-8"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FILTERS.ALL_AGENTS')
            }}</label>
            <select
              v-model="filters.user_id"
              class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 h-8"
            >
              <option value="">{{ t('CONVERSIONS.FILTERS.ALL_AGENTS') }}</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.name }}
              </option>
            </select>
          </div>
          <button
            @click="applyFilters"
            class="px-4 py-1.5 bg-n-brand text-white rounded-lg text-sm font-medium h-8"
          >
            {{ t('CONVERSIONS.FILTERS.TITLE') }}
          </button>
          <button
            @click="clearFilters"
            class="px-4 py-1.5 border border-n-weak text-n-slate-11 rounded-lg text-sm h-8"
          >
            {{ t('CONVERSIONS.CLEAR') }}
          </button>
        </div>

        <!-- Financial summary -->
        <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div
            class="bg-gradient-to-br from-emerald-50 to-emerald-100/60 border border-emerald-200 rounded-xl p-4 md:col-span-2"
          >
            <p class="text-xs font-medium text-emerald-700 mb-1">
              {{ t('CONVERSIONS.FINANCIAL.TOTAL_VALUE') }}
            </p>
            <p class="text-2xl font-bold text-emerald-700">
              {{ formatCurrency(listTotals.value) }}
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.FINANCIAL.AVG_VALUE') }}
            </p>
            <p class="text-xl font-bold text-n-slate-12">
              {{ formatCurrency(listTotals.avg_value) }}
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.TOTAL_COUNT') }}
            </p>
            <p class="text-xl font-bold text-n-slate-12">
              {{ listTotals.count || totalCount }}
            </p>
            <p class="text-xs text-n-slate-9 mt-0.5">
              <span class="text-blue-600">{{ listTotals.crm_count }} CRM</span>
              <span class="mx-1">/</span>
              <span class="text-emerald-600"
                >{{ listTotals.agenda_count }} Agenda</span
              >
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.SOURCE_CRM') }} R$
            </p>
            <p class="text-xl font-bold text-blue-600">
              {{ formatCurrency(listTotals.crm_value) }}
            </p>
          </div>
        </div>

        <!-- Loading -->
        <div
          v-if="loadingConversions"
          class="flex items-center justify-center py-16"
        >
          <span
            class="i-lucide-loader-circle size-6 text-n-slate-10 animate-spin"
          />
        </div>

        <!-- Empty -->
        <div
          v-else-if="conversions.length === 0"
          class="flex flex-col items-center justify-center py-16 text-n-slate-10"
        >
          <span class="i-lucide-trending-up size-10 mb-3 opacity-40" />
          <p class="text-sm">{{ t('CONVERSIONS.NO_CONVERSIONS') }}</p>
        </div>

        <!-- Table -->
        <div
          v-else
          class="bg-n-alpha-1 border border-n-weak rounded-xl overflow-hidden"
        >
          <table class="w-full text-sm">
            <thead class="bg-n-alpha-2">
              <tr>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.CONTACT') }}
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.TYPE') }}
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.SOURCE') }}
                </th>
                <th
                  class="text-right px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.VALUE') }}
                </th>
                <th
                  class="text-left px-4 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.DATE') }}
                </th>
                <th class="px-4 py-3" />
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="c in conversions"
                :key="c.id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-4 py-3">
                  <div v-if="c.contact" class="flex items-center gap-2">
                    <img
                      v-if="c.contact.thumbnail"
                      :src="c.contact.thumbnail"
                      class="size-7 rounded-full object-cover"
                    />
                    <span
                      v-else
                      class="size-7 rounded-full bg-n-alpha-3 flex items-center justify-center text-xs font-semibold text-n-slate-11"
                    >
                      {{ c.contact.name?.charAt(0)?.toUpperCase() }}
                    </span>
                    <div>
                      <p class="font-medium text-n-slate-12">
                        {{ c.contact.name }}
                      </p>
                      <p
                        v-if="c.contact.phone_number"
                        class="text-xs text-n-slate-10"
                      >
                        {{ c.contact.phone_number }}
                      </p>
                    </div>
                  </div>
                  <span v-else class="text-n-slate-10">—</span>
                </td>
                <td class="px-4 py-3">
                  <span
                    v-if="c.conversion_type"
                    class="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
                    :style="{
                      backgroundColor: c.conversion_type.color + '20',
                      color: c.conversion_type.color,
                    }"
                  >
                    <span
                      class="size-1.5 rounded-full"
                      :style="{ backgroundColor: c.conversion_type.color }"
                    />
                    {{ c.conversion_type.name }}
                  </span>
                  <span v-else class="text-n-slate-10 text-xs">—</span>
                </td>
                <td class="px-4 py-3">
                  <span
                    class="text-xs font-medium px-2 py-0.5 rounded-full"
                    :class="sourceColor(c.source_type)"
                  >
                    {{ sourceLabel(c.source_type) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right font-semibold text-n-slate-12">
                  {{ c.value ? formatCurrency(c.value) : '—' }}
                </td>
                <td class="px-4 py-3 text-n-slate-11 text-xs">
                  {{ formatDate(c.converted_at) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center justify-end gap-1">
                    <button
                      @click="openEditConversion(c)"
                      class="p-1 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-n-slate-12 transition-colors"
                    >
                      <span class="i-lucide-pencil size-3.5" />
                    </button>
                    <button
                      @click="deleteConversion(c)"
                      class="p-1 rounded hover:bg-red-50 text-n-slate-10 hover:text-red-600 transition-colors"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div
            v-if="totalPages > 1"
            class="flex items-center justify-between px-4 py-3 border-t border-n-weak"
          >
            <p class="text-xs text-n-slate-10">
              {{
                t('CONVERSIONS.PAGINATION', {
                  current: currentPage,
                  total: totalPages,
                })
              }}
            </p>
            <div class="flex gap-2">
              <button
                :disabled="currentPage <= 1"
                @click="
                  currentPage--;
                  fetchConversions();
                "
                class="px-3 py-1 text-xs border border-n-weak rounded-lg disabled:opacity-40 hover:bg-n-alpha-2"
              >
                {{ t('CONVERSIONS.PREV') }}
              </button>
              <button
                :disabled="currentPage >= totalPages"
                @click="
                  currentPage++;
                  fetchConversions();
                "
                class="px-3 py-1 text-xs border border-n-weak rounded-lg disabled:opacity-40 hover:bg-n-alpha-2"
              >
                {{ t('CONVERSIONS.NEXT') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ANALYTICS TAB -->
      <div v-if="activeTab === 'analytics'" class="p-6">
        <!-- Source filter -->
        <div class="flex gap-2 mb-6">
          <button
            v-for="opt in [
              { id: 'all', label: t('CONVERSIONS.ALL') },
              { id: 'PipelineItem', label: t('CONVERSIONS.SOURCE_CRM') },
              {
                id: 'AppointmentBooking',
                label: t('CONVERSIONS.SOURCE_AGENDA'),
              },
            ]"
            :key="opt.id"
            @click="analyticsSeparated = opt.id"
            class="px-4 py-1.5 text-sm rounded-lg border transition-colors"
            :class="
              analyticsSeparated === opt.id
                ? 'bg-n-brand text-white border-n-brand'
                : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
            "
          >
            {{ opt.label }}
          </button>
        </div>

        <!-- Totals cards -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.TOTAL_COUNT') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ analyticsData.totals?.count || 0 }}
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.TOTAL_VALUE') }}
            </p>
            <p class="text-2xl font-bold text-emerald-600">
              {{ formatCurrency(analyticsData.totals?.value) }}
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.AVG_VALUE') }}
            </p>
            <p class="text-2xl font-bold text-blue-600">
              {{ formatCurrency(analyticsData.totals?.avg_value) }}
            </p>
          </div>
          <div class="bg-n-alpha-1 border border-n-weak rounded-xl p-4">
            <p class="text-xs text-n-slate-10 mb-1">
              {{ t('CONVERSIONS.ANALYTICS.CRM_AGENDA_LABEL') }}
            </p>
            <p class="text-xl font-bold text-n-slate-12">
              <span class="text-blue-600">{{
                analyticsData.totals?.crm_count || 0
              }}</span>
              <span class="text-n-slate-9 mx-1">/</span>
              <span class="text-emerald-600">{{
                analyticsData.totals?.agenda_count || 0
              }}</span>
            </p>
          </div>
        </div>

        <!-- Pipeline Performance -->
        <div
          class="bg-n-alpha-1 border border-n-weak rounded-xl overflow-hidden mb-6"
        >
          <div
            class="px-5 py-4 border-b border-n-weak"
          >
            <div class="flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-n-slate-12">
                  {{ t('CONVERSIONS.ANALYTICS.PIPELINE_PERFORMANCE') }}
                </h3>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{
                    pipelineMode === 'period'
                      ? 'Leads que passaram por cada etapa no período selecionado'
                      : 'Quantidade atual de leads em cada etapa'
                  }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="text-[10px] font-medium px-2 py-0.5 rounded-full"
                  :class="
                    pipelineMode === 'period'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-emerald-100 text-emerald-700'
                  "
                >
                  {{ pipelineMode === 'period' ? 'Período' : 'Atual' }}
                </span>
                <span class="i-lucide-kanban size-5 text-n-slate-9" />
              </div>
            </div>
            <!-- Date filters -->
            <div class="flex items-center gap-2 mt-3">
              <div class="flex items-center gap-1.5">
                <label class="text-xs text-n-slate-10">De</label>
                <input
                  v-model="pipelineFilters.start_date"
                  type="date"
                  class="text-xs border border-n-weak rounded-lg px-2 py-1 bg-n-background text-n-slate-12 h-7"
                />
              </div>
              <div class="flex items-center gap-1.5">
                <label class="text-xs text-n-slate-10">Até</label>
                <input
                  v-model="pipelineFilters.end_date"
                  type="date"
                  class="text-xs border border-n-weak rounded-lg px-2 py-1 bg-n-background text-n-slate-12 h-7"
                />
              </div>
              <button
                @click="applyPipelineFilters"
                class="px-3 py-1 bg-n-brand text-white rounded-lg text-xs font-medium h-7"
              >
                Filtrar
              </button>
              <button
                v-if="pipelineFilters.start_date || pipelineFilters.end_date"
                @click="clearPipelineFilters"
                class="px-3 py-1 border border-n-weak text-n-slate-10 rounded-lg text-xs h-7"
              >
                Limpar
              </button>
              <span class="w-px h-5 bg-n-weak mx-1" />
              <button
                @click="pipelinePerformance.forEach(p => { expandedPipelines[p.pipeline_id] = true })"
                class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 rounded text-xs h-7 flex items-center gap-1"
                title="Expandir todos"
              >
                <span class="i-lucide-chevrons-down size-3.5" />
              </button>
              <button
                @click="expandedPipelines = {}"
                class="px-2 py-1 text-n-slate-10 hover:text-n-slate-12 rounded text-xs h-7 flex items-center gap-1"
                title="Recolher todos"
              >
                <span class="i-lucide-chevrons-up size-3.5" />
              </button>
            </div>
          </div>
          <div
            v-if="loadingPipelinePerf"
            class="flex items-center justify-center py-10"
          >
            <span
              class="i-lucide-loader-circle size-5 text-n-slate-10 animate-spin"
            />
            <span class="ml-2 text-sm text-n-slate-10">{{
              t('CONVERSIONS.ANALYTICS.LOADING_PIPELINE')
            }}</span>
          </div>
          <div
            v-else-if="!pipelinePerformance.length"
            class="flex items-center justify-center py-10 text-n-slate-10 text-sm"
          >
            {{ t('CONVERSIONS.ANALYTICS.NO_PIPELINE_DATA') }}
          </div>
          <div v-else class="divide-y divide-n-weak">
            <div
              v-for="pipe in pipelinePerformance"
              :key="pipe.pipeline_id"
              class="px-6 py-5"
            >
              <!-- Pipeline header (clickable to toggle) -->
              <button
                class="flex items-center justify-between w-full text-left group"
                @click="expandedPipelines[pipe.pipeline_id] = !expandedPipelines[pipe.pipeline_id]"
              >
                <div class="flex items-center gap-2">
                  <span
                    class="size-4 transition-transform duration-200"
                    :class="[
                      expandedPipelines[pipe.pipeline_id] ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right',
                      'text-n-slate-9'
                    ]"
                  />
                  <span class="i-lucide-git-branch size-4 text-n-slate-9" />
                  <p class="font-semibold text-n-slate-12 group-hover:text-n-brand transition-colors">
                    {{ pipe.pipeline_name }}
                  </p>
                  <span
                    v-if="pipe.pipeline_folder"
                    class="text-[10px] text-n-slate-9 bg-n-alpha-2 px-1.5 py-0.5 rounded"
                  >
                    {{ pipe.pipeline_folder }}
                  </span>
                </div>
                <div class="flex items-center gap-5 text-sm">
                  <div class="flex items-center gap-1.5 text-n-slate-10">
                    <span
                      class="i-lucide-trending-up size-4 text-emerald-500"
                    />
                    <span
                      ><strong class="text-n-slate-12">{{
                        pipe.total_conversions
                      }}</strong>
                      conversões</span
                    >
                  </div>
                  <div
                    v-if="pipe.total_value > 0"
                    class="flex items-center gap-1.5"
                  >
                    <span
                      class="i-lucide-circle-dollar-sign size-4 text-blue-500"
                    />
                    <span class="font-semibold text-blue-600">{{
                      formatCurrency(pipe.total_value)
                    }}</span>
                  </div>
                  <span class="text-xs text-n-slate-9">
                    {{ pipe.stages?.reduce((sum, s) => sum + s.entries_count, 0) }} leads
                  </span>
                </div>
              </button>

              <!-- Funnel visualization (collapsible) -->
              <div
                v-show="expandedPipelines[pipe.pipeline_id]"
                class="flex flex-col items-center gap-0 mx-auto mt-6"
                style="max-width: 700px"
              >
                <template
                  v-for="(stage, idx) in pipe.stages"
                  :key="stage.stage_id"
                >
                  <!-- Stage bar -->
                  <div
                    class="relative flex items-center justify-between rounded-lg px-5 py-3.5 transition-all"
                    :style="{
                      width: funnelWidth(stage, pipe.stages) + '%',
                      minWidth: '220px',
                      backgroundColor: hexToRgba(stage.stage_color, 0.13),
                      border: `1.5px solid ${hexToRgba(stage.stage_color, 0.35)}`,
                      borderLeft: `5px solid ${stage.stage_color || '#6366f1'}`,
                    }"
                  >
                    <!-- Left: name + entries -->
                    <div>
                      <p
                        class="text-sm font-semibold text-n-slate-12 leading-tight"
                      >
                        {{ stage.stage_name }}
                      </p>
                      <p class="text-xs text-n-slate-9 mt-0.5">
                        {{ stage.entries_count }}
                        {{ stage.entries_count === 1 ? 'lead' : 'leads' }}
                        <span v-if="pipelineMode === 'period'" class="text-n-slate-8">no período</span>
                      </p>
                    </div>

                    <!-- Right: conversions + value -->
                    <div class="flex items-center gap-4 ml-4">
                      <div
                        v-if="stage.conversions_count > 0"
                        class="text-right"
                      >
                        <p
                          class="text-lg font-bold text-emerald-600 leading-none"
                        >
                          {{ stage.conversions_count }}
                        </p>
                        <p class="text-[11px] text-n-slate-9 mt-0.5">
                          conversões
                        </p>
                      </div>
                      <div v-if="stage.total_value > 0" class="text-right">
                        <p
                          class="text-base font-bold text-blue-600 leading-none"
                        >
                          {{ formatCurrency(stage.total_value) }}
                        </p>
                        <p class="text-[11px] text-n-slate-9 mt-0.5">receita</p>
                      </div>
                      <!-- Won/Lost badge -->
                      <span
                        v-if="stage.is_won"
                        class="i-lucide-trophy size-4 text-emerald-500"
                        title="Etapa de ganho"
                      />
                      <span
                        v-if="stage.is_lost"
                        class="i-lucide-x-circle size-4 text-red-400"
                        title="Etapa de perda"
                      />
                    </div>
                  </div>

                  <!-- Drop-off connector -->
                  <div
                    v-if="idx < pipe.stages.length - 1"
                    class="flex flex-col items-center gap-0.5 py-1.5"
                  >
                    <span class="i-lucide-chevron-down size-4 text-n-slate-8" />
                    <span
                      v-if="stage.advance_rate !== null && stage.advance_rate !== undefined"
                      class="text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                      :class="
                        stage.advance_rate >= 50
                          ? 'bg-emerald-100 text-emerald-700'
                          : stage.advance_rate >= 20
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-600'
                      "
                    >
                      {{ stage.advance_rate }}% avança
                    </span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Top leads -->
        <div
          class="bg-n-alpha-1 border border-n-weak rounded-xl overflow-hidden"
        >
          <div class="px-5 py-4 border-b border-n-weak">
            <h3 class="font-semibold text-n-slate-12">
              {{ t('CONVERSIONS.ANALYTICS.BY_LEAD') }}
            </h3>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ t('CONVERSIONS.ANALYTICS.TOP_LEADS_SUBTITLE') }}
            </p>
          </div>
          <div
            v-if="loadingAnalytics"
            class="flex items-center justify-center py-12"
          >
            <span
              class="i-lucide-loader-circle size-6 text-n-slate-10 animate-spin"
            />
          </div>
          <div
            v-else-if="!analyticsData.by_contact?.length"
            class="flex items-center justify-center py-12 text-n-slate-10 text-sm"
          >
            {{ t('CONVERSIONS.NO_DATA') }}
          </div>
          <table v-else class="w-full text-sm">
            <thead class="bg-n-alpha-2">
              <tr>
                <th
                  class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  #
                </th>
                <th
                  class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.CONTACT') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.TOTAL_COUNT') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.TOTAL_VALUE') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.AVG_VALUE') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  CRM / Agenda
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="(lead, index) in analyticsData.by_contact"
                :key="lead.contact_id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-5 py-3 text-n-slate-10 font-semibold">
                  {{ index + 1 }}
                </td>
                <td class="px-5 py-3">
                  <p class="font-medium text-n-slate-12">
                    {{ lead.contact_name }}
                  </p>
                  <p v-if="lead.contact_phone" class="text-xs text-n-slate-10">
                    {{ lead.contact_phone }}
                  </p>
                </td>
                <td class="px-5 py-3 text-right font-semibold text-n-slate-12">
                  {{ lead.total_conversions }}
                </td>
                <td class="px-5 py-3 text-right font-semibold text-emerald-600">
                  {{ formatCurrency(lead.total_value) }}
                </td>
                <td class="px-5 py-3 text-right text-n-slate-11">
                  {{ formatCurrency(lead.avg_value) }}
                </td>
                <td class="px-5 py-3 text-right text-xs">
                  <span class="text-blue-600 font-semibold">{{
                    lead.crm_conversions
                  }}</span>
                  <span class="text-n-slate-9 mx-1">/</span>
                  <span class="text-emerald-600 font-semibold">{{
                    lead.agenda_conversions
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- By agent -->
        <div
          class="bg-n-alpha-1 border border-n-weak rounded-xl overflow-hidden mt-6"
        >
          <div class="px-5 py-4 border-b border-n-weak">
            <h3 class="font-semibold text-n-slate-12">
              {{ t('CONVERSIONS.ANALYTICS.BY_AGENT') }}
            </h3>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ t('CONVERSIONS.ANALYTICS.BY_AGENT_SUBTITLE') }}
            </p>
          </div>
          <div
            v-if="loadingAnalytics"
            class="flex items-center justify-center py-12"
          >
            <span
              class="i-lucide-loader-circle size-6 text-n-slate-10 animate-spin"
            />
          </div>
          <div
            v-else-if="!analyticsData.by_agent?.length"
            class="flex items-center justify-center py-12 text-n-slate-10 text-sm"
          >
            {{ t('CONVERSIONS.NO_DATA') }}
          </div>
          <table v-else class="w-full text-sm">
            <thead class="bg-n-alpha-2">
              <tr>
                <th
                  class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  #
                </th>
                <th
                  class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.AGENT') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.TOTAL_COUNT') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.TOTAL_VALUE') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  {{ t('CONVERSIONS.AVG_VALUE') }}
                </th>
                <th
                  class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide"
                >
                  CRM / Agenda
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="(row, index) in analyticsData.by_agent"
                :key="row.user_id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-5 py-3 text-n-slate-10 font-semibold">
                  {{ index + 1 }}
                </td>
                <td class="px-5 py-3">
                  <p class="font-medium text-n-slate-12">{{ row.user_name }}</p>
                </td>
                <td class="px-5 py-3 text-right font-semibold text-n-slate-12">
                  {{ row.total_conversions }}
                </td>
                <td class="px-5 py-3 text-right font-semibold text-emerald-600">
                  {{ formatCurrency(row.total_value) }}
                </td>
                <td class="px-5 py-3 text-right text-n-slate-11">
                  {{ formatCurrency(row.avg_value) }}
                </td>
                <td class="px-5 py-3 text-right text-xs">
                  <span class="text-blue-600 font-semibold">{{
                    row.crm_conversions
                  }}</span>
                  <span class="text-n-slate-9 mx-1">/</span>
                  <span class="text-emerald-600 font-semibold">{{
                    row.agenda_conversions
                  }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- By product -->
        <div
          v-if="analyticsData.by_product?.length"
          class="bg-n-alpha-1 border border-n-weak rounded-xl overflow-hidden mt-6"
        >
          <div class="px-5 py-4 border-b border-n-weak">
            <h3 class="font-semibold text-n-slate-12">
              {{ t('CONVERSIONS.ANALYTICS.BY_PRODUCT') }}
            </h3>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ t('CONVERSIONS.ANALYTICS.BY_PRODUCT_SUBTITLE') }}
            </p>
          </div>
          <table class="w-full text-sm">
            <thead class="bg-n-alpha-2">
              <tr>
                <th class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">#</th>
                <th class="text-left px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">Produto</th>
                <th class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">{{ t('CONVERSIONS.TOTAL_COUNT') }}</th>
                <th class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">{{ t('CONVERSIONS.TOTAL_VALUE') }}</th>
                <th class="text-right px-5 py-3 text-xs font-semibold text-n-slate-10 uppercase tracking-wide">{{ t('CONVERSIONS.AVG_VALUE') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="(row, index) in analyticsData.by_product"
                :key="row.product_id"
                class="hover:bg-n-alpha-1 transition-colors"
              >
                <td class="px-5 py-3 text-n-slate-10 font-semibold">{{ index + 1 }}</td>
                <td class="px-5 py-3">
                  <div class="flex items-center gap-2">
                    <span
                      class="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      :style="{ backgroundColor: row.product_color || '#6366f1' }"
                    >{{ row.product_code }}</span>
                    <p class="font-medium text-n-slate-12">{{ row.product_name }}</p>
                  </div>
                </td>
                <td class="px-5 py-3 text-right font-semibold text-n-slate-12">{{ row.total_conversions }}</td>
                <td class="px-5 py-3 text-right font-semibold text-emerald-600">{{ formatCurrency(row.total_value) }}</td>
                <td class="px-5 py-3 text-right text-n-slate-11">{{ formatCurrency(row.avg_value) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TYPES TAB -->
      <div v-if="activeTab === 'types'" class="p-6">
        <!-- Type Form -->
        <div
          v-if="showTypeForm"
          class="bg-n-alpha-1 border border-n-weak rounded-xl p-5 mb-6"
        >
          <h3 class="font-semibold text-n-slate-12 mb-4">
            {{
              editingType
                ? t('CONVERSIONS.TYPES.EDIT_TYPE')
                : t('CONVERSIONS.TYPES.NEW_TYPE')
            }}
          </h3>
          <div class="grid grid-cols-2 gap-4">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11"
                >{{ t('CONVERSIONS.TYPES.NAME') }} *</label
              >
              <input
                v-model="typeForm.name"
                type="text"
                class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
              />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">{{
                t('CONVERSIONS.TYPES.SOURCE_CATEGORY')
              }}</label>
              <select
                v-model="typeForm.source_category"
                class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
              >
                <option value="general">
                  {{ t('CONVERSIONS.TYPES.SOURCE_GENERAL') }}
                </option>
                <option value="agenda">
                  {{ t('CONVERSIONS.TYPES.SOURCE_AGENDA') }}
                </option>
                <option value="crm">
                  {{ t('CONVERSIONS.TYPES.SOURCE_CRM') }}
                </option>
              </select>
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-n-slate-11">{{
                t('CONVERSIONS.TYPES.COLOR')
              }}</label>
              <div class="flex items-center gap-2">
                <input
                  v-model="typeForm.color"
                  type="color"
                  class="h-9 w-16 rounded border border-n-weak cursor-pointer"
                />
                <input
                  v-model="typeForm.color"
                  type="text"
                  class="flex-1 border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
                />
              </div>
            </div>
            <div class="flex flex-col gap-1 col-span-2">
              <label class="text-xs font-medium text-n-slate-11">{{
                t('CONVERSIONS.TYPES.DESCRIPTION')
              }}</label>
              <input
                v-model="typeForm.description"
                type="text"
                class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
              />
            </div>
            <div
              v-if="typeForm.source_category === 'agenda'"
              class="flex flex-col gap-2 col-span-2"
            >
              <label class="text-xs font-medium text-n-slate-11">{{
                t('CONVERSIONS.TYPES.AGENDA_TRIGGER_STATUSES')
              }}</label>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="status in agendaBookingStatusOptions"
                  :key="status.value"
                  class="flex items-center gap-2 rounded-lg border border-n-weak px-3 py-2 text-sm text-n-slate-12"
                >
                  <input
                    v-model="typeForm.agenda_booking_statuses"
                    type="checkbox"
                    :value="status.value"
                    class="size-4 rounded"
                  />
                  {{ status.label }}
                </label>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ t('CONVERSIONS.TYPES.AGENDA_TRIGGER_HELP') }}
              </p>
            </div>
            <div class="flex items-center gap-3">
              <input
                v-model="typeForm.has_monetary_value"
                type="checkbox"
                id="has-monetary"
                class="size-4 rounded"
              />
              <label for="has-monetary" class="text-sm text-n-slate-12">{{
                t('CONVERSIONS.TYPES.HAS_MONETARY_VALUE')
              }}</label>
            </div>
          </div>
          <div class="flex gap-3 mt-5">
            <button
              @click="saveType"
              class="px-5 py-2 bg-n-brand text-white rounded-lg text-sm font-medium hover:opacity-90"
            >
              {{ t('CONVERSIONS.FORM.SAVE') }}
            </button>
            <button
              @click="showTypeForm = false"
              class="px-5 py-2 border border-n-weak text-n-slate-11 rounded-lg text-sm hover:bg-n-alpha-2"
            >
              {{ t('CONVERSIONS.FORM.CANCEL') }}
            </button>
          </div>
        </div>

        <!-- Types list -->
        <div v-if="loadingTypes" class="flex items-center justify-center py-12">
          <span
            class="i-lucide-loader-circle size-6 text-n-slate-10 animate-spin"
          />
        </div>
        <div
          v-else-if="conversionTypes.length === 0 && !showTypeForm"
          class="flex flex-col items-center justify-center py-16 text-n-slate-10"
        >
          <span class="i-lucide-tag size-10 mb-3 opacity-40" />
          <p class="text-sm">{{ t('CONVERSIONS.TYPES.NO_TYPES') }}</p>
        </div>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          <div
            v-for="type in conversionTypes"
            :key="type.id"
            class="bg-n-alpha-1 border border-n-weak rounded-xl p-4 flex items-start justify-between"
          >
            <div class="flex items-start gap-3">
              <span
                class="size-4 rounded-full mt-0.5 flex-shrink-0"
                :style="{ backgroundColor: type.color }"
              />
              <div>
                <p class="font-semibold text-n-slate-12 text-sm">
                  {{ type.name }}
                </p>
                <p
                  v-if="type.description"
                  class="text-xs text-n-slate-10 mt-0.5"
                >
                  {{ type.description }}
                </p>
                <p class="text-xs text-n-slate-10 mt-1">
                  <span>{{ conversionTypeSourceLabel(type) }}</span>
                  <span class="mx-2">•</span>
                  {{
                    type.has_monetary_value
                      ? t('CONVERSIONS.TYPES.WITH_MONETARY')
                      : t('CONVERSIONS.TYPES.WITHOUT_MONETARY')
                  }}
                  <span class="ml-2 text-n-slate-9">{{
                    t('CONVERSIONS.TYPES.CONVERSIONS_COUNT', {
                      count: type.conversions_count || 0,
                    })
                  }}</span>
                </p>
                <p
                  v-if="
                    type.source_category === 'agenda' &&
                    type.agenda_booking_statuses?.length
                  "
                  class="text-xs text-n-slate-9 mt-1"
                >
                  {{
                    t('CONVERSIONS.TYPES.AGENDA_TRIGGER_PREVIEW', {
                      statuses: type.agenda_booking_statuses.join(', '),
                    })
                  }}
                </p>
              </div>
            </div>
            <div class="flex gap-1 flex-shrink-0">
              <button
                @click="openEditType(type)"
                class="p-1.5 rounded hover:bg-n-alpha-3 text-n-slate-10 hover:text-n-slate-12"
              >
                <span class="i-lucide-pencil size-3.5" />
              </button>
              <button
                @click="deleteType(type)"
                class="p-1.5 rounded hover:bg-red-50 text-n-slate-10 hover:text-red-600"
              >
                <span class="i-lucide-trash-2 size-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conversion Form Modal -->
    <div
      v-if="showConversionForm"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-n-background rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 class="font-semibold text-n-slate-12 text-lg mb-5">
          {{
            editingConversion
              ? t('CONVERSIONS.EDIT_CONVERSION')
              : t('CONVERSIONS.NEW_CONVERSION')
          }}
        </h3>
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FORM.TYPE_LABEL')
            }}</label>
            <select
              v-model="conversionForm.conversion_type_id"
              class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
            >
              <option value="">
                {{ t('CONVERSIONS.FORM.TYPE_PLACEHOLDER') }}
              </option>
              <option v-for="ct in conversionTypes" :key="ct.id" :value="ct.id">
                {{ ct.name }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FORM.VALUE_LABEL')
            }}</label>
            <input
              v-model="conversionForm.value"
              type="number"
              step="0.01"
              min="0"
              :placeholder="t('CONVERSIONS.FORM.VALUE_PLACEHOLDER')"
              class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FORM.DATE_LABEL')
            }}</label>
            <input
              v-model="conversionForm.converted_at"
              type="date"
              class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('CONVERSIONS.FORM.NOTES_LABEL')
            }}</label>
            <textarea
              v-model="conversionForm.notes"
              rows="3"
              :placeholder="t('CONVERSIONS.FORM.NOTES_PLACEHOLDER')"
              class="border border-n-weak rounded-lg px-3 py-2 text-sm bg-n-background text-n-slate-12 resize-none"
            />
          </div>
        </div>
        <div class="flex gap-3 mt-6">
          <button
            @click="saveConversion"
            :disabled="savingConversion"
            class="flex-1 py-2.5 bg-n-brand text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-60"
          >
            {{ t('CONVERSIONS.FORM.SAVE') }}
          </button>
          <button
            @click="showConversionForm = false"
            class="flex-1 py-2.5 border border-n-weak text-n-slate-11 rounded-lg text-sm hover:bg-n-alpha-2"
          >
            {{ t('CONVERSIONS.FORM.CANCEL') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
